import React from 'react';
import { Box } from '@mui/material';

export const DEFAULT_TEMP_LIMIT = 75;
export const DEFAULT_SCALE_MAX = 110;

/** A single temperature threshold drawn on the bar (e.g. a Pour2Limit). */
export interface TempLimitMarker {
  /** Threshold temperature in °C. */
  temp: number;
  /** Marker colour — typically the limit's configured BackgroundColor. */
  color?: string;
  /** Short tooltip label, typically the limit's Purpose. */
  label?: string;
}

interface TemperatureProfileBarProps {
  currentTemp?: number;
  maxTemp?: number;
  referenceMax: number;
  simple?: boolean;
  /** Full set of temperature limits to reference on the bar. Takes precedence
   *  over the single `tempLimit` when provided. */
  limits?: TempLimitMarker[];
  tempLimit?: number;
  scaleMax?: number;
  showScale?: boolean;
  /** Render the fixed 0..scaleMax scale even when there are no limit markers
   *  yet (used by the group editor preview, which is driven by the bar
   *  indicator's scaleMax alone). */
  forceScale?: boolean;
  /** Smoothly animate fill width changes (true for live dashboards). Set false
   *  for interactive scrubbing so the bar tracks the input instantly instead of
   *  chasing each value over a 0.4s transition. */
  animate?: boolean;
  /** Compact max-temp indicator: a small caret marker at the max position
   *  instead of the full black line + dot. Pairs with `simple`. */
  maxMarker?: boolean;
  /** Flat single-colour fill instead of the green-to-red gradient. Used for
   *  ambient (External Temperature and Humidity) pours, where no temperature
   *  limits apply and a fill trending toward red would imply a threshold
   *  that doesn't exist. */
  flat?: boolean;
  sx?: { height?: number; [key: string]: any };
}

const DOT_CEILING = 92;

const GRADIENT_COLORS = '#A8D86E, #C6DE41, #E8E44A, #F5D14B, #F2A65A, #ED8A6A, #E87A8A';
const DANGER_COLOR = '#D32F2F';
const FLAT_COLOR = '#A8D86E';

export const TemperatureProfileBar: React.FC<TemperatureProfileBarProps> = ({
  currentTemp,
  maxTemp,
  referenceMax,
  simple = false,
  limits,
  tempLimit,
  scaleMax,
  showScale = false,
  forceScale = false,
  animate = true,
  maxMarker = false,
  flat = false,
  sx = {},
}) => {
  const barHeight = sx?.height ?? 28;
  const dotSize = Math.max(10, Math.min(barHeight * 0.5, 14));
  // Pixel-snap the max-temp line: centring a 2px line with top:50% +
  // translateY(-50%) lands on a fractional device-pixel row on some bars,
  // so it anti-aliases and renders ~1px/blurry while others stay crisp.
  // An integer top + whole-pixel height keeps every bar's line identical.
  const lineThickness = 2;
  const lineTop = Math.round((barHeight - lineThickness) / 2);
  const { height: _h, ...restSx } = sx || {};

  // Normalise the limit markers: prefer the explicit `limits` list, falling
  // back to the single `tempLimit` for legacy callers. Sorted ascending; the
  // HIGHEST threshold drives the solid danger-overflow fill — the bar only
  // turns solid red past the top limit, not the first one.
  const markers: TempLimitMarker[] = (
    limits && limits.length
      ? limits
      : tempLimit != null && tempLimit > 0
        ? [{ temp: tempLimit }]
        : []
  )
    .filter((m) => m.temp != null && m.temp > 0)
    .slice()
    .sort((a, b) => a.temp - b.temp);

  const useLimits = (markers.length > 0 || forceScale) && scaleMax != null && scaleMax > 0;
  // Highest marker drives the solid danger fill; undefined when there are no
  // markers (e.g. the editor preview, which only has a forced scale).
  const dangerLimit = markers.length > 0 ? markers[markers.length - 1].temp : undefined;
  const effectiveRef = useLimits ? scaleMax! : referenceMax;
  const safeRef = effectiveRef > 0 ? effectiveRef : 1;

  const ceiling = simple || useLimits ? 100 : DOT_CEILING;

  const fillPct =
    currentTemp != null && currentTemp > 0
      ? Math.min((currentTemp / safeRef) * ceiling, 100)
      : 0;

  const dotPct =
    maxTemp != null && maxTemp > 0
      ? Math.min((maxTemp / safeRef) * ceiling, 100)
      : 0;

  let gradient: string;
  let bgSize: string;

  if (flat) {
    gradient = FLAT_COLOR;
    bgSize = '100% 100%';
  } else if (useLimits && dangerLimit != null && currentTemp != null && currentTemp > 0) {
    const limitRatio = dangerLimit / currentTemp;
    if (limitRatio >= 1) {
      gradient = `linear-gradient(to right, ${GRADIENT_COLORS})`;
      bgSize = `${(limitRatio * 100).toFixed(2)}% 100%`;
    } else {
      const endPct = (limitRatio * 100).toFixed(2);
      gradient = `linear-gradient(to right, #A8D86E 0%, #C6DE41, #E8E44A, #F5D14B, #F2A65A, #ED8A6A, #E87A8A ${endPct}%, ${DANGER_COLOR} ${endPct}%)`;
      bgSize = '100% 100%';
    }
  } else {
    gradient = `linear-gradient(to right, ${GRADIENT_COLORS})`;
    bgSize = fillPct > 0 ? `${(10000 / fillPct).toFixed(2)}% 100%` : '100% 100%';
  }

  const showDot = !simple && !maxMarker && maxTemp != null && maxTemp > 0;

  return (
    <Box sx={{ width: '100%', ...restSx }}>
      <Box
        sx={{
          position: 'relative',
          height: barHeight,
          width: '100%',
          bgcolor: 'grey.200',
          borderRadius: 2,
          overflow: simple ? 'hidden' : 'visible',
          ...(!simple && {
            // Hover feedback lives on the whole track so it's identical on
            // every bar, regardless of how much (or whether) the temperature
            // fill is drawn. Previously the highlight sat on the fill itself,
            // so a short/absent fill (low or missing current temp) gave a
            // smaller hover than fuller bars.
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease',
            '&:hover': {
              boxShadow: '0 1px 5px rgba(0,0,0,0.22)',
            },
            '&:has(.temp-dot:hover) .temp-line': {
              height: '3px !important',
              boxShadow: '0 0 4px rgba(0,0,0,0.3)',
            },
          }),
        }}
      >
        {/* Gradient fill */}
        {fillPct > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              borderRadius: 2,
              transition: animate ? 'width 0.4s ease' : 'none',
              background: gradient,
              backgroundSize: bgSize,
              backgroundPosition: 'left center',
              backgroundRepeat: 'no-repeat',
              zIndex: 1,
            }}
            style={{ width: `${fillPct}%` }}
            title={`Current: ${currentTemp?.toFixed(1)} °C`}
          />
        )}

        {/* Temperature limit markers — one coloured line per configured limit */}
        {useLimits &&
          markers.map((m, i) => {
            const pct = Math.min((m.temp / safeRef) * ceiling, 100);
            if (pct > 100) return null;
            return (
              <Box
                key={`limit-${m.temp}-${i}`}
                className="temp-limit"
                sx={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  width: '3px',
                  transform: 'translateX(-50%)',
                  bgcolor: m.color ? undefined : 'error.main',
                  opacity: 0.85,
                  zIndex: 4,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease, width 0.15s ease',
                  '&:hover': {
                    opacity: 1,
                    width: '4px',
                  },
                }}
                style={{
                  left: `${pct}%`,
                  ...(m.color ? { backgroundColor: m.color } : {}),
                }}
                title={`${m.label ? `${m.label}: ` : 'Limit: '}${m.temp} °C`}
              />
            );
          })}

        {/* Compact max-temp marker — small caret, no line/dot */}
        {maxMarker && maxTemp != null && maxTemp > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '8px solid rgba(0,0,0,0.6)',
              zIndex: 5,
              cursor: 'default',
              transition: 'border-top-color 0.12s ease',
              '&:hover': {
                borderTop: '8px solid rgba(0,0,0,1)',
              },
            }}
            style={{ left: `${dotPct}%` }}
            title={`Max: ${maxTemp.toFixed(1)} °C`}
          />
        )}

        {/* Line from left edge to max-temp dot */}
        {showDot && (
          <Box
            className="temp-line"
            sx={{
              position: 'absolute',
              bgcolor: 'common.black',
              zIndex: 2,
              pointerEvents: 'none',
              transition: 'height 0.15s ease, box-shadow 0.15s ease',
              // Force GPU compositing so the hairline rasterises on the pixel grid.
              transform: 'translateZ(0)',
            }}
            style={{
              left: 0,
              width: `${dotPct}%`,
              top: lineTop,
              height: lineThickness,
            }}
          />
        )}

        {/* Max temperature dot */}
        {showDot && (
          <Box
            className="temp-dot"
            sx={{
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: dotSize,
              height: dotSize,
              borderRadius: '50%',
              bgcolor: 'common.black',
              cursor: 'pointer',
              zIndex: 3,
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                transform: 'translate(-50%, -50%) scale(1.4)',
                boxShadow: '0 0 0 4px rgba(0,0,0,0.15)',
              },
            }}
            style={{ left: `${dotPct}%` }}
            title={`Max: ${maxTemp?.toFixed(1)} °C`}
          />
        )}
      </Box>

      {/* Temperature scale */}
      {showScale && useLimits && (
        <Box sx={{ position: 'relative', width: '100%', height: 10, mt: '1px' }}>
          {/* Base axis ticks */}
          {[0, 25, 50, 75, 100, scaleMax!].map((temp) => {
            const pct = (temp / safeRef) * ceiling;
            if (pct > 100) return null;
            const isFirst = temp === 0;
            const isLast = temp === scaleMax;
            return (
              <Box
                key={`tick-${temp}`}
                sx={{
                  position: 'absolute',
                  transform: isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                  fontSize: '0.55rem',
                  lineHeight: 1,
                  color: 'text.disabled',
                  userSelect: 'none',
                }}
                style={{ left: `${pct}%` }}
              >
                {temp}
              </Box>
            );
          })}
          {/* Configured limit values, coloured to match their marker lines */}
          {markers.map((m, i) => {
            const pct = (m.temp / safeRef) * ceiling;
            if (pct > 100) return null;
            return (
              <Box
                key={`limit-tick-${m.temp}-${i}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  transform: 'translateX(-50%)',
                  fontSize: '0.55rem',
                  lineHeight: 1,
                  fontWeight: 700,
                  color: m.color || 'error.main',
                  userSelect: 'none',
                }}
                style={{ left: `${pct}%`, ...(m.color ? { color: m.color } : {}) }}
              >
                {m.temp}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
